import React, { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Paperclip, Scale, MessageSquare, Clock, AlertTriangle, Search, History, X, Download } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCSV } from "@/lib/csv";

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtd = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

const STATUSES = [
  { key: "all", label: "All" },
  { key: "flagged", label: "Needs review" },
  { key: "open", label: "Open" },
  { key: "reviewed", label: "Reviewed" },
  { key: "dismissed", label: "Dismissed" },
];
const SORTS = [
  { key: "created_desc", label: "Newest first" },
  { key: "created_asc", label: "Oldest first" },
  { key: "status", label: "By status" },
  { key: "collector", label: "By collector" },
];
const DECISIONS = [
  { key: "pending", label: "Under review" },
  { key: "no_violation", label: "No violation" },
  { key: "warning_issued", label: "Warning issued" },
  { key: "collector_suspended", label: "Account action taken" },
];
const DECISION_LABEL = Object.fromEntries(DECISIONS.map((d) => [d.key, d.label]));

const statusCls = (s) =>
  s === "open" ? "bg-[#B91C1C]/10 text-[#B91C1C]" :
  s === "reviewed" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" :
  "bg-[#1E3F33]/10 text-[#5C6B64]";

const Attachments = ({ list, testid }) => (
  <div className="mt-2 flex flex-wrap gap-2" data-testid={testid}>
    {list.map((a, i) => (
      <a key={i} href={`${API}/files/${a.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#CC5A3A] hover:underline border border-[#1E3F33]/10 rounded-lg px-2.5 py-1.5">
        <Paperclip className="w-3.5 h-3.5" /> {a.original_name || `Attachment ${i + 1}`}
      </a>
    ))}
  </div>
);

function CollectorHistoryDialog({ collectorId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open && collectorId) {
      setData(null);
      api.get(`/admin/reports/collector/${collectorId}`).then(({ data }) => setData(data)).catch(() => setData({ items: [] }));
    }
  }, [open, collectorId]);
  const near = data && data.upheld_count >= data.threshold - 1 && data.upheld_count < data.threshold;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="collector-history-dialog" className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1E3F33] flex items-center gap-2"><History className="w-5 h-5 text-[#2D6A4F]" /> {data?.business_name || "Collector"} — complaint history</DialogTitle>
          <DialogDescription className={near ? "text-[#B91C1C] font-medium" : "text-[#5C6B64]"} data-testid="collector-history-summary">
            {!data ? "Loading complaint history…" : `${data.upheld_count} of ${data.threshold} upheld complaint(s) before auto-suspension${near ? " — one more will auto-suspend this collector." : "."}`}
          </DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-[#5C6B64] text-sm">Loading…</div> : (
          <ul className="space-y-3">
            {data.items.map((r) => (
              <li key={r.id} className="border border-[#1E3F33]/10 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-[#8A9A86]">{fmtd(r.created_at)} · by {r.customer_name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusCls(r.status)}`}>{r.status}</span>
                </div>
                <p className="text-sm text-[#1E3F33] mt-1.5">{r.explanation}</p>
                <div className="text-[11px] text-[#5C6B64] mt-1">Decision: {DECISION_LABEL[r.decision] || r.decision}</div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReportCard({ r, onPatch, selected, onToggleSelect, onOpenHistory }) {
  const [decision, setDecision] = useState(r.decision || "pending");
  const [note, setNote] = useState(r.decision_note || "");
  const [notify, setNotify] = useState(false);
  const [suspend, setSuspend] = useState(false);
  const saveDecision = () => {
    const body = { decision, note, notify, suspend };
    if (r.status === "open") body.status = "reviewed";
    onPatch(r.id, body);
  };
  return (
    <div className="card-soft" data-testid={`admin-report-${r.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <input type="checkbox" data-testid={`admin-report-select-${r.id}`} checked={selected} onChange={() => onToggleSelect(r.id)} className="mt-1.5 rounded border-[#1E3F33]/30" />
          <div>
            <button onClick={() => onOpenHistory(r.collector_id)} data-testid={`admin-report-collector-${r.id}`} className="font-heading text-lg text-[#1E3F33] hover:text-[#CC5A3A] inline-flex items-center gap-1.5">
              {r.collector_business_name || "Collector"} <History className="w-3.5 h-3.5 opacity-60" />
            </button>
            <div className="text-xs text-[#8A9A86] mt-0.5">
              Reported by {r.customer_name || "Customer"}{r.customer_email ? ` · ${r.customer_email}` : ""}{r.customer_phone ? ` · ${r.customer_phone}` : ""} · {fmt(r.created_at)}
            </div>
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusCls(r.status)}`} data-testid={`admin-report-status-${r.id}`}>{r.status}</span>
      </div>

      {r.auto_flagged && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#B91C1C] bg-[#B91C1C]/10 rounded-full px-2.5 py-1" data-testid={`admin-report-flagged-${r.id}`}>
          <AlertTriangle className="w-3.5 h-3.5" /> Needs review — collector didn't respond within 14 days
        </div>
      )}

      <p className="text-sm text-[#1E3F33] mt-3 whitespace-pre-wrap">{r.explanation}</p>
      {(r.attachments || []).length > 0 && <Attachments list={r.attachments} testid={`admin-report-attachments-${r.id}`} />}

      <div className="mt-4 rounded-xl bg-[#F0EFEA] p-3" data-testid={`admin-report-response-${r.id}`}>
        <div className="text-xs font-medium text-[#1E3F33] flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Collector response</div>
        {r.response ? (
          <>
            <p className="text-sm text-[#5C6B64] mt-1.5 whitespace-pre-wrap">{r.response.text}</p>
            <div className="text-[10px] text-[#8A9A86] mt-1">Responded {fmt(r.response.responded_at)}</div>
            {(r.response.attachments || []).length > 0 && <Attachments list={r.response.attachments} testid={`admin-response-attachments-${r.id}`} />}
          </>
        ) : (
          <div className="mt-1.5 text-xs text-[#8A9A86] inline-flex items-center gap-1.5" data-testid={`admin-report-noresponse-${r.id}`}>
            <Clock className="w-3.5 h-3.5" />
            {r.response_window_open ? (r.respond_by ? `Awaiting collector response — due by ${fmt(r.respond_by)}` : "Awaiting collector response") : "No response — the 14-day window has closed"}
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-[#1E3F33]/10 pt-3">
        <div className="text-xs font-medium text-[#1E3F33] flex items-center gap-1.5 mb-2"><Scale className="w-3.5 h-3.5" /> Case decision</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select data-testid={`admin-report-decision-select-${r.id}`} value={decision} onChange={(e) => setDecision(e.target.value)} className="input-soft sm:max-w-[220px] text-sm">
            {DECISIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <input data-testid={`admin-report-decision-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note (shared with both parties if notifying)" className="input-soft flex-1 text-sm" />
          <button data-testid={`admin-report-decision-save-${r.id}`} onClick={saveDecision} className="btn-primary text-xs py-2 px-4 shrink-0">Save decision</button>
        </div>
        <label className="mt-2 flex items-center gap-2 text-xs text-[#5C6B64] cursor-pointer">
          <input type="checkbox" data-testid={`admin-report-notify-${r.id}`} checked={notify} onChange={(e) => setNotify(e.target.checked)} className="rounded border-[#1E3F33]/30" />
          Notify collector &amp; customer of this decision and reason
        </label>
        <label className="mt-1.5 flex items-center gap-2 text-xs text-[#B91C1C] cursor-pointer font-medium">
          <input type="checkbox" data-testid={`admin-report-suspend-${r.id}`} checked={suspend} onChange={(e) => setSuspend(e.target.checked)} className="rounded border-[#B91C1C]/40" />
          Suspend this collector's account when saving
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid={`admin-report-reviewed-${r.id}`} onClick={() => onPatch(r.id, { status: "reviewed" })} disabled={r.status === "reviewed"} className="btn-secondary text-xs py-1.5 px-3 disabled:opacity-40">Mark reviewed</button>
        <button data-testid={`admin-report-dismissed-${r.id}`} onClick={() => onPatch(r.id, { status: "dismissed" })} disabled={r.status === "dismissed"} className="text-xs py-1.5 px-3 rounded-lg text-[#5C6B64] hover:bg-[#1E3F33]/5 disabled:opacity-40">Dismiss</button>
        {r.status !== "open" && (
          <button data-testid={`admin-report-reopen-${r.id}`} onClick={() => onPatch(r.id, { status: "open" })} className="text-xs py-1.5 px-3 rounded-lg text-[#B91C1C] hover:bg-[#B91C1C]/5">Reopen</button>
        )}
      </div>
    </div>
  );
}

export default function AdminReports() {
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState("3");
  const [selected, setSelected] = useState([]);
  const [historyCollector, setHistoryCollector] = useState(null);
  const [collectorId, setCollectorId] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [collectorOptions, setCollectorOptions] = useState([]);

  useEffect(() => {
    api.get("/admin/abuse-settings").then(({ data }) => setThreshold(String(data.auto_suspend_threshold))).catch(() => {});
    // Build the collector filter list from all reports (one-time).
    api.get("/admin/reports").then(({ data }) => {
      const seen = {};
      (data.items || []).forEach((r) => { if (r.collector_id && !seen[r.collector_id]) seen[r.collector_id] = r.collector_business_name || "Collector"; });
      setCollectorOptions(Object.entries(seen).map(([id, name]) => ({ id, name })));
    }).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    const params = { q: q || undefined, sort };
    if (filter !== "all" && filter !== "flagged") params.status = filter;
    if (collectorId) params.collector_id = collectorId;
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    api.get("/admin/reports", { params })
      .then(({ data }) => {
        setItems(filter === "flagged" ? data.items.filter((r) => r.auto_flagged) : data.items);
        setOpenCount(data.open_count);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filter, q, sort, collectorId, range]);

  const exportCSV = () => {
    if (!items.length) { toast.error("Nothing to export"); return; }
    const rows = items.map((r) => ({
      collector: r.collector_business_name || "",
      customer: r.customer_name || "",
      customer_email: r.customer_email || "",
      date: fmt(r.created_at),
      status: r.status,
      decision: DECISION_LABEL[r.decision] || r.decision,
      responded: r.collector_responded ? "yes" : "no",
      explanation: (r.explanation || "").replace(/\s+/g, " "),
    }));
    downloadCSV("abuse-reports.csv", rows);
    toast.success(`Exported ${rows.length} report(s)`);
  };

  const saveThreshold = async () => {
    const n = parseInt(threshold, 10);
    if (!n || n < 1) { toast.error("Enter a number of 1 or more"); return; }
    try { await api.put("/admin/abuse-settings", { auto_suspend_threshold: n }); toast.success(`Collectors auto-suspend after ${n} upheld complaint(s)`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/reports/${id}`, body);
      if (data.suspended) toast.warning(`Collector account suspended${data.auto_suspended ? ` (after ${data.upheld_count} upheld complaint(s))` : ""}.`);
      else toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggleSelect = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const bulk = async (status) => {
    try {
      const { data } = await api.post("/admin/reports/bulk", { ids: selected, status });
      toast.success(`${data.modified} report(s) marked ${status}`);
      setSelected([]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="container-x py-12" data-testid="admin-reports">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2"><Flag className="w-6 h-6 text-[#B91C1C]" /> Abuse reports ({items.length})</h1>
          <p className="text-[#5C6B64] mt-2">Review customer complaints and collector responses, then decide the case.</p>
        </div>
        <div className="text-right"><div className="text-xs uppercase tracking-wide text-[#8A9A86]">Open</div><div className="font-heading text-2xl text-[#B91C1C]" data-testid="admin-reports-open-count">{openCount}</div></div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2 rounded-xl border border-[#1E3F33]/10 bg-[#F0EFEA] px-4 py-3" data-testid="abuse-settings">
        <Scale className="w-4 h-4 text-[#2D6A4F]" />
        <span className="text-sm text-[#1E3F33]">Auto-suspend a collector after</span>
        <input type="number" min="1" data-testid="abuse-threshold-input" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-16 rounded-lg border border-[#1E3F33]/15 bg-white px-2 py-1 text-sm text-[#1E3F33] focus:outline-none focus:border-[#2D6A4F]" />
        <span className="text-sm text-[#1E3F33]">upheld complaint(s).</span>
        <button data-testid="abuse-threshold-save" onClick={saveThreshold} className="btn-secondary text-xs py-1.5 px-3">Save</button>
      </div>

      {/* Search + sort + collector filter + export */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#8A9A86] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="admin-reports-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search collector, customer, or complaint text…" className="input-soft pl-9 text-sm" />
        </div>
        <select data-testid="admin-reports-collector-filter" value={collectorId} onChange={(e) => setCollectorId(e.target.value)} className="input-soft text-sm sm:max-w-[200px]">
          <option value="">All collectors</option>
          {collectorOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="admin-reports-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="input-soft text-sm sm:max-w-[160px]">
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button data-testid="admin-reports-export-csv" onClick={exportCSV} disabled={!items.length} className="btn-secondary inline-flex items-center gap-2 text-sm py-2 px-3 disabled:opacity-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="mt-3"><DateRangeFilter onChange={(start, end) => setRange({ start, end })} /></div>

      <div className="mt-4 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s.key} data-testid={`admin-reports-filter-${s.key}`} onClick={() => setFilter(s.key)} className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${filter === s.key ? "bg-[#1E3F33] text-white border-[#1E3F33]" : "bg-white text-[#1E3F33] border-[#1E3F33]/15 hover:border-[#1E3F33]/40"}`}>{s.label}</button>
        ))}
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl bg-[#1E3F33] text-white px-4 py-2.5" data-testid="admin-reports-bulk-bar">
          <span className="text-sm">{selected.length} selected</span>
          <button data-testid="admin-reports-bulk-reviewed" onClick={() => bulk("reviewed")} className="text-xs bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5">Mark reviewed</button>
          <button data-testid="admin-reports-bulk-dismissed" onClick={() => bulk("dismissed")} className="text-xs bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5">Dismiss</button>
          <button data-testid="admin-reports-bulk-clear" onClick={() => setSelected([])} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100"><X className="w-3.5 h-3.5" /> Clear</button>
        </div>
      )}

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : items.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A9A86]" data-testid="admin-reports-empty">No reports match.</p>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((r) => <ReportCard key={r.id} r={r} onPatch={patch} selected={selected.includes(r.id)} onToggleSelect={toggleSelect} onOpenHistory={setHistoryCollector} />)}
        </div>
      )}

      <CollectorHistoryDialog collectorId={historyCollector} open={!!historyCollector} onOpenChange={(o) => { if (!o) setHistoryCollector(null); }} />
    </div>
  );
}
